<template>
  <div class="surat-keluar-list">
    <div class="main-container">
      <div class="page-header">
        <h2 class="page-title">Surat Keluar</h2>
      </div>

      <!-- Filters Section -->

      <div class="actions-bar">
        <a-input
          v-model:value="searchText"
          placeholder="Cari nomor surat, jenis surat, atau perihal"
          style="width: 60%"
          @input="handleSearchChange"
        >
          <template #prefix>
            <SearchOutlined style="color: rgba(0, 0, 0, 0.25)" />
          </template>
        </a-input>
        <a-input
          v-model:value="creatorName"
          placeholder="Cari nama pengaju"
          style="width: 30%"
          @input="handleCreatorNameChange"
        >
          <template #prefix>
            <SearchOutlined style="color: rgba(0, 0, 0, 0.25)" />
          </template>
        </a-input>

        <!-- TAMBAH button only for sekretaris -->
        <a-button
          v-if="canCreateSurat"
          type="primary"
          size="large"
          class="btn-tambah"
          @click="handleTambah"
        >
          <template #icon>
            <PlusOutlined />
          </template>
          TAMBAH
        </a-button>
      </div>

      <a-table
        :columns="columns"
        :data-source="filteredList"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1400 }"
        :custom-row="customRow"
        @change="handleTableChange"
        row-key="id"
        class="surat-table"
      >
        <template #filterDropdown="{   column }">
          <div v-if="column.key === 'tanggalSurat'" style="padding: 8px">
            <a-range-picker
              v-model:value="dateRange"
              format="DD-MM-YYYY"
              :placeholder="['Tanggal Mulai', 'Tanggal Akhir']"
              style="width: 280px; margin-bottom: 8px; display: block"
              @change="handleDateFilterChange"
            />
          </div>
        </template>

        <template #nomorSurat="{ text }">
          <span>{{ text }}</span>
        </template>

        <template #tanggalSurat="{ text }">
          {{ formatDate(text) }}
        </template>

        <template #createdAt="{ text }">
          {{ formatDateTime(text) }}
        </template>

        <template #perihal="{ record }">
          {{ getPerihal(record) }}
        </template>

        <template #sifatSurat="{ text }">
          <a-tag v-if="text" :color="getSifatColor(text)">
            {{ text }}
          </a-tag>
          <span v-else>-</span>
        </template>

        <template #status="{ text }">
          <a-tag v-if="text" :style="getStatusStyle(text)" class="status-tag-custom">
            <strong>{{ text === 'Revisi' ? 'Revisi Verifikator' : text }}</strong>
          </a-tag>
          <span v-else>-</span>
        </template>
      </a-table>

      <!-- Total Info -->
      <!-- <div class="total-info">Total {{ pagination.total || filteredList.length }} baris</div> -->
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed, reactive, h, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { message } from 'ant-design-vue'
import { SearchOutlined, PlusOutlined } from '@ant-design/icons-vue'
import { suratKeluarAPI, userAPI } from '@/services/axios/perki'
import moment from 'moment'
import 'moment/locale/id'

moment.locale('id')

export default defineComponent({
  name: 'SuratKeluarList',
  components: {
    SearchOutlined,
    PlusOutlined,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const loading = ref(false)
    const suratList = ref([])
    const searchText = ref('')
    const creatorName = ref('')
    const dateRange = ref(null)
    const selectedPengaju = ref(null)
    const sekretarisList = ref([])

    // Get current user info from localStorage
    const currentUser = JSON.parse(localStorage.getItem('perki_user') || '{}')
    const currentUserId = currentUser.id
    const currentUserRole = localStorage.getItem('perki_role') || currentUser.role

    // Only sekretaris (or super_admin) can create new surat keluar
    const canCreateSurat = computed(() => {
      return ['sekretaris', 'super_admin'].includes(currentUserRole)
    })

    // Check if user can edit surat (only sekretaris who owns it, or super_admin)
    const canEditSurat = record => {
      const isOwner = record.creator?.id === currentUserId
      // Only super_admin has full access
      if (currentUserRole === 'super_admin') {
        return true
      }
      // Sekretaris can only edit their own surat
      return currentUserRole === 'sekretaris' && isOwner
    }

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      showTotal: (total, range) => `Total ${total} baris`,
      total: 0,
    })

    // Debounce timers for search and creator inputs
    const searchTimer = ref(null)
    const creatorTimer = ref(null)

    // Computed: Sekretaris filter options
    const sekretarisFilterOptions = computed(() => {
      if (!Array.isArray(sekretarisList.value)) {
        return []
      }
      return sekretarisList.value.map(user => ({
        text: user.name,
        value: user.id,
      }))
    })

    // Table columns configuration - Removed Jenis Surat filter, Added Perihal column
    const columns = computed(() => [
      {
        title: 'No. Surat',
        dataIndex: 'nomorSurat',
        key: 'nomorSurat',
        width: 180,
        sorter: (a, b) => (a.nomorSurat || '').localeCompare(b.nomorSurat || ''),
        slots: { customRender: 'nomorSurat' },
      },
      {
        title: 'Tanggal Surat',
        dataIndex: 'tanggalSurat',
        key: 'tanggalSurat',
        width: 150,
        slots: {
          customRender: 'tanggalSurat',
          filterDropdown: 'filterDropdown', // <--- MUST BE 'filterDropdown' for AntDV 2.x
        },
        filteredValue: dateRange.value ? [dateRange.value] : null,
      },
      {
        title: 'Jenis Surat',
        dataIndex: 'jenisSurat',
        key: 'jenisSurat',
        width: 150,
      },
      {
        title: 'Perihal',
        dataIndex: 'perihal',
        key: 'perihal',
        width: 250,
        ellipsis: true,
        slots: { customRender: 'perihal' },
      },
      {
        title: 'Waktu Dibuat/Unggah',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        defaultSortOrder: 'descend',
        slots: { customRender: 'createdAt' },
      },
      {
        title: 'Pengaju Surat',
        dataIndex: ['creator', 'name'],
        key: 'creator',
        width: 180,
        filters: sekretarisFilterOptions.value,
        onFilter: (value, record) => record.creator?.id === value,
      },
      {
        title: 'Status Surat',
        dataIndex: 'status',
        key: 'status',
        width: 150,
        filters: [
          { text: 'Draft', value: 'Draft' },
          { text: 'Dibatalkan', value: 'Dibatalkan' },
          { text: 'Verifikasi', value: 'Verifikasi' },
          { text: 'Revisi Verifikator', value: 'Revisi' },
          { text: 'Revisi Penandatangan', value: 'Revisi Penandatangan' },
          { text: 'Penandatanganan', value: 'Penandatanganan' },
          { text: 'Belum Dikirim', value: 'Belum Dikirim' },
          { text: 'Terkirim', value: 'Terkirim' },
        ],
        filteredValue: state.filteredInfo?.status || null,
        onFilter: (value, record) => record.status === value,
        slots: { customRender: 'status' },
      },
    ])

    // Get Perihal - For Surat Tugas use namaKegiatan, for Surat Lainnya use namaKegiatan as perihal
    const getPerihal = record => {
      return record.namaKegiatan || record.perihal || '-'
    }

    const state = reactive({
      filteredInfo: null,
      sortedInfo: null,
    })

    // Frontend-based filtering (only pengaju/status/date filters are applied locally).
    // Searching by nomorSurat, jenisSurat, and perihal is handled server-side via `q`.
    // --- REVISED filteredList COMPUTED PROPERTY ---
    const filteredList = computed(() => {
      let list = suratList.value || []
      // NOTE: We DO NOT filter by `searchText` here because `q` is sent to the
      // server to search nomorSurat, jenisSurat, and perihal. Local filtering
      // would incorrectly exclude server-matching items (e.g. perihal matches
      // but pengaju does not). Local filters below are for date/creator/status.

      // 2. Filter by Date Range (New - Logic moved here)
      if (dateRange.value && dateRange.value.length === 2) {
        const startDate = dateRange.value[0].startOf('day')
        const endDate = dateRange.value[1].endOf('day')

        list = list.filter(item => {
          if (!item.tanggalSurat) return false
          return moment(item.tanggalSurat).isBetween(startDate, endDate, null, '[]')
        })
      }

      // 3. Filter by Pengaju/Creator
      // NOTE: This is now applied server-side. We used to filter locally by
      // creator id (from table filters), but the API now accepts `creatorIds`
      // (CSV) so we do not apply a local filter here to avoid double-filtering
      // and issues with paginated server results.

      // 4. Filter by Status (If you want that too)
      if (state.filteredInfo?.status && state.filteredInfo.status.length > 0) {
        list = list.filter(item => {
          return state.filteredInfo.status.includes(item.status)
        })
      }

      // Sort
      return [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    })

    // Fetch surat keluar list (server-side pagination, limited results)
    const fetchSuratKeluar = async () => {
      loading.value = true
      try {
        // Determine status filter if applied (support multiple statuses as CSV)
        // The table filter may return multiple statuses, e.g. ['Draft','Verifikasi']
        const statusFilter =
          state.filteredInfo?.status && state.filteredInfo.status.length > 0
            ? state.filteredInfo.status.join(',')
            : undefined

        // Build query params (include new dateFrom/dateTo if date range is selected)
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...(statusFilter ? { status: statusFilter } : {}),
        }

        if (dateRange.value && dateRange.value.length === 2) {
          // API expects DD-MM-YYYY format. dateTo should be inclusive (backend treats it as end of day).
          params.dateFrom = dateRange.value[0].format('DD-MM-YYYY')
          params.dateTo = dateRange.value[1].format('DD-MM-YYYY')
        }

        // Add q param for server-side search by nomorSurat, jenisSurat, or perihal
        // (case-insensitive, partial)
        if (searchText.value && searchText.value.trim()) {
          params.q = searchText.value.trim()
        }

        // Add creatorQ param for server-side partial search on creator name
        // (case-insensitive, partial). Backend may JOIN users when this is used.
        if (creatorName.value && creatorName.value.trim()) {
          params.creatorQ = creatorName.value.trim()
        }

        // Add creatorIds param (CSV) when table filter selects creators
        if (state.filteredInfo?.creator && state.filteredInfo.creator.length > 0) {
          params.creatorIds = state.filteredInfo.creator.join(',')
        }

        // Sorting: determine sortBy and sortOrder from state.sortedInfo
        // Supported sortBy: 'createdAt' (default) or 'nomorSurat'
        // Supported sortOrder: 'ASC' | 'DESC' (default 'DESC')
        let sortBy = 'createdAt'
        let sortOrder = 'DESC'
        if (state.sortedInfo && state.sortedInfo.field) {
          const f = state.sortedInfo.field
          if (f === 'nomorSurat') sortBy = 'nomorSurat'
          else sortBy = 'createdAt'
        }
        if (state.sortedInfo && state.sortedInfo.order) {
          if (state.sortedInfo.order === 'ascend') sortOrder = 'ASC'
          else if (state.sortedInfo.order === 'descend') sortOrder = 'DESC'
          else {
            // Invalid sort order — validate before sending
            message.error('Invalid sortOrder')
            loading.value = false
            return
          }
        }

        params.sortBy = sortBy
        params.sortOrder = sortOrder

        // Debug: show params being sent so it's easy to verify in console/network
        console.debug('fetchSuratKeluar params:', params)
        const response = await suratKeluarAPI.getAll(params)

        if (response.data.status === 'success') {
          suratList.value = response.data.data.suratKeluar || []
          const p = response.data.data.pagination || {}
          pagination.current = p.page || pagination.current
          pagination.pageSize = p.pageSize || pagination.pageSize
          pagination.total = p.totalRecords || 0
        }
      } catch (error) {
        console.error('Error fetching surat keluar:', error)
        message.error('Gagal memuat data surat keluar')
      } finally {
        loading.value = false
      }
    }

    // Fetch sekretaris users for pengaju filter
    const fetchSekretarisList = async () => {
      try {
        const response = await userAPI.getUsersInMyGroup({ role: 'sekretaris' })
        if (response.data?.status === 'success') {
          // Backend returns { status, message, data: { users: [...] } }
          sekretarisList.value = response.data.data?.users || []
        }
      } catch (error) {
        console.error('Error fetching sekretaris list:', error)
        sekretarisList.value = [] // Ensure it's always an array
      }
    }

    // Filter option for select search
    const filterOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Handle search input change (live filtering)
    const handleSearchChange = () => {
      pagination.current = 1
      // Debounce server-side search using `q` param
      if (searchTimer.value) {
        clearTimeout(searchTimer.value)
      }
      searchTimer.value = setTimeout(() => {
        fetchSuratKeluar()
      }, 300)
    }

    // Handle creator name input change (server-side filter by creatorName)
    const handleCreatorNameChange = () => {
      pagination.current = 1
      if (creatorTimer.value) clearTimeout(creatorTimer.value)
      creatorTimer.value = setTimeout(() => {
        fetchSuratKeluar()
      }, 300)
    }

    onBeforeUnmount(() => {
      if (searchTimer.value) clearTimeout(searchTimer.value)
      if (creatorTimer.value) clearTimeout(creatorTimer.value)
    })

    // Handle date range filter change
    const handleDateFilterChange = () => {
      pagination.current = 1
      // When date range changes, request filtered data from backend
      fetchSuratKeluar()
    }

    // Handle date range change (kept for compatibility, now unused)
    const handleDateRangeChange = () => {
      pagination.current = 1
    }

    // Handle pengaju filter change (kept for compatibility, now unused)
    const handlePengajuChange = () => {
      pagination.current = 1
    }

    // Handle table change (filters, sorter, pagination)
    const handleTableChange = (pag, filters, sorter) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      state.filteredInfo = filters
      state.sortedInfo = sorter

      // For server-side pagination/filtering, refetch data when table changes
      fetchSuratKeluar()
    }

    // Handle detail click - navigate based on status, bentukSurat, and ownership
    const handleDetail = record => {
      const { id, status, bentukSurat } = record

      // For Hard Copy: Use detail-hard-copy page for archived statuses (Belum Dikirim, Terkirim)
      // This allows users to manage bukti pengiriman for non-pengurus
      const hardCopyArchivedStatuses = ['Belum Dikirim', 'Terkirim']
      if (bentukSurat === 'Hard copy' && hardCopyArchivedStatuses.includes(status)) {
        router.push(`/surat-keluar/${id}/detail-hc`)
        return
      }

      // Use view page for cancelled status (both Hard Copy and Soft Copy)
      if (status === 'Dibatalkan') {
        router.push(`/surat-keluar/${id}/view`)
        return
      }

      // Only sekretaris who owns the surat can edit, or admin/super_admin
      // All other roles get read-only view
      if (!canEditSurat(record)) {
        router.push(`/surat-keluar/${id}/view`)
        return
      }

      // For Hard Copy with Verifikasi, Revisi, or Penandatanganan status, use detail-hard-copy page
      const hardCopyDetailStatuses = ['Verifikasi', 'Revisi', 'Penandatanganan']
      if (bentukSurat === 'Hard copy' && hardCopyDetailStatuses.includes(status)) {
        router.push(`/surat-keluar/${id}/detail-hc`)
        return
      }

      // For Soft Copy with completed statuses, use view page
      const softCopyViewStatuses = ['Belum Dikirim', 'Terkirim']
      if (bentukSurat === 'Soft copy' && softCopyViewStatuses.includes(status)) {
        router.push(`/surat-keluar/${id}/view`)
        return
      }

      // Default: use edit page (Draft, Revisi Penandatangan, or Soft Copy Verifikasi/Revisi/Penandatanganan)
      router.push(`/surat-keluar/${id}`)
    }

    // Handle tambah button
    const handleTambah = () => {
      router.push('/surat-keluar/create')
    }

    // Custom row - make entire row clickable
    const customRow = record => {
      return {
        onClick: () => {
          handleDetail(record)
        },
      }
    }

    // Format date (3 November 2025)
    const formatDate = dateString => {
      if (!dateString) return '-'
      return moment(dateString).format('D MMMM YYYY')
    }

    // Format datetime (Wednesday 2025-11-05 10:39)
    const formatDateTime = dateString => {
      if (!dateString) return '-'
      return moment(dateString).format('dddd YYYY-MM-DD HH:mm')
    }

    // Get sifat color
    const getSifatColor = sifat => {
      const colors = {
        Biasa: 'success',
        Terbatas: 'processing',
        Rahasia: 'warning',
        'Sangat Rahasia': 'error',
      }
      return colors[sifat] || 'default'
    }

    // Get status color (aligned with Verifikasi / Report Dinas pages)
    const getStatusColor = status => {
      const colors = {
        Draft: 'warning',
        Terkirim: 'cyan',
        Dibatalkan: 'default',
        Verifikasi: 'red', // needs attention / pending verification
        Penandatanganan: 'cyan',
        Revisi: 'orange',
        'Revisi Penandatangan': 'orange',
        'Belum Dikirim': 'orange',
      }
      return colors[status] || 'default'
    }

    // Get status style (color, background, border). Border color == base color.
    const getStatusStyle = status => {
      const s = String(status || '')
      const palette = {
        Draft: { base: '#FAAE13', bg: '#FFFBE6' },
        Terkirim: { base: '#51A622', bg: '#F6FFED' },
        Dibatalkan: { base: '#D9D9D9', bg: '#FAFAFA' },
        Verifikasi: { base: '#0957D9', bg: '#E5F4FF' },
        Penandatanganan: { base: '#0B979D', bg: '#E5FFFB' },
        Revisi: { base: '#541CAB', bg: '#FAF1FF' },
        'Revisi Penandatangan': { base: '#C41E7F', bg: '#FFF0F6' },
        'Belum Dikirim': { base: '#D57117', bg: '#FFF7E5' },
      }
      const c = palette[s] || { base: '#d9d9d9', bg: '#f5f5f5' }
      return {
        color: c.base,
        backgroundColor: c.bg,
        border: `1px solid ${c.base}`,
      }
    }

    // Apply status filter from query params (when navigating from dashboard)
    const applyStatusFilter = () => {
      const statusParam = route.query.status
      if (statusParam) {
        // Support comma-separated status in query param, e.g. ?status=Draft,Verifikasi
        const statuses = Array.isArray(statusParam)
          ? statusParam
          : String(statusParam)
              .split(',')
              .map(s => s.trim())
        // Set the filteredInfo to apply the status filter
        state.filteredInfo = { status: statuses }
        // Ensure pagination resets to first page
        pagination.current = 1
      }
    }

    onMounted(() => {
      applyStatusFilter()
      fetchSuratKeluar()
      fetchSekretarisList()
    })

    return {
      loading,
      suratList,
      filteredList,
      searchText,
      creatorName,
      dateRange,
      selectedPengaju,
      sekretarisList,
      columns,
      pagination,
      canCreateSurat,
      getPerihal,
      filterOption,
      handleSearchChange,
      handleCreatorNameChange,
      handleDateFilterChange,
      handleDateRangeChange,
      handlePengajuChange,
      handleTableChange,
      handleDetail,
      handleTambah,
      customRow,
      formatDate,
      formatDateTime,
      getSifatColor,
      getStatusColor,
      getStatusStyle,
      // expose fetch to allow manual refresh
      fetchSuratKeluar,
    }
  },
})
</script>

<style lang="scss" scoped>
.surat-keluar-list {
  background: #fcfcfc;
  min-height: 100vh;

  .main-container {
    padding: 24px;
    background: #fcfcfc;
    min-height: calc(100vh - 64px);

    @media (max-width: 768px) {
      padding: 16px;
    }

    @media (max-width: 576px) {
      padding: 12px;
    }
  }
}

.page-header {
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin: 0;

    @media (max-width: 576px) {
      font-size: 20px;
    }
  }
}

/* --- KEMBALI KE STYLE LAMA (Flexbox) --- */
.actions-bar {
  display: flex;
  justify-content: space-between; /* Input di kiri, tombol di kanan */
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  @media (max-width: 576px) {
    gap: 8px;
  }

  /* Styling khusus untuk input search agar rounded */
  :deep(.ant-input-search) {
    .ant-input-affix-wrapper {
      border-radius: 8px;
      padding: 8px 12px;
    }
  }

  /* Input search full width on mobile */
  > .ant-input,
  > .ant-input-affix-wrapper {
    @media (max-width: 768px) {
      width: 100% !important;
    }
  }

  /* Styling tombol tambah */
  .btn-tambah {
    background: #e01f28;
    border-color: #e01f28;
    border-radius: 50px; /* Sedikit lebih rounded menyesuaikan desain baru */
    font-weight: 600;
    height: 40px;
    padding: 0 20px;
    flex-shrink: 0; /* Mencegah tombol gepeng saat layar kecil */

    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
    }

    &:hover {
      background: white !important;
      border-color: #e10f28 !important;
      color: #e10f28 !important;
    }
  }
}

.surat-table {
  :deep(.ant-table) {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    .ant-table-thead > tr > th {
      background: #f2f4f8;
      font-weight: 600;
      color: #262626;
      border-bottom: 1px solid #e8e8e8;
    }

    .ant-table-tbody > tr > td {
      background: #ffffff;
    }

    .ant-table-tbody > tr {
      cursor: pointer;

      &:hover > td {
        background: #fafafa;
      }
    }
  }
}

.total-info {
  margin-top: 16px;
  color: #8c8c8c;
  font-size: 13px;

  @media (max-width: 576px) {
    font-size: 12px;
    margin-top: 12px;
  }
}

.status-tag-custom {
  border-radius: 50px !important;
  padding: 4px 12px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
}
</style>
